import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { Button, Confirm, Modal, SpinnerOverlay, Tooltip } from '@components/common';
import FormWithTabs from '@components/common/FormWithTabs';

import { useMutateCheckUniqueOrganisationValues } from '@hooks/organisation/useMutateCheckUniqueOrganisationValues';
import { useOrganisationsFormData } from '@hooks/organisation/useOrganisationsFormData';

import { getRoleAvailabilityByOrganisationType } from '@utils/organisation';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { TCheckUniqOrganisations } from '@interfaces/components/form';
import { ICountry, IOrganisationFormValues, IOrganisationProfile } from '@interfaces/organisation/organisations';

import { EOrganizationType } from '@enums/common';
import { EValidationMode } from '@enums/validationMode';

import { modalCloseConfirm } from '@constants/modals';
import {
	blockTooltipTextForPlatformOwner,
	defaultValuesCheckErrors,
	questionRevokedRoles,
	tabsNames,
} from './constants';

import Address from './Address';
import BasicInfo from './BasicInfo';
import OrganisationType from './OrganisationType';
import RoleAvailability from './RoleAvailability';
import { schema } from './schema';

import styles from './OrganisationForm.module.scss';

interface IOrganisationFormProps {
	isEditMode?: boolean;
	toggleOrganisationModal?: () => void;
	closeModals?: () => void;
	checkUniqueValues?: (params: {
		name: string;
		legalName: string;
		email: string | null;
		switchToNext: () => void;
	}) => void;
	submitHandler?: any;
	isLoadingForm?: boolean;
	isSettings?: boolean;
	profile?: IOrganisationProfile;
	setIsDirtyFormModal: React.Dispatch<React.SetStateAction<boolean>>;
	isDirtyFormModal?: boolean;
}

const OrganisationForm = ({
	isEditMode = false,
	toggleOrganisationModal = () => null,
	checkUniqueValues = () => null,
	closeModals = () => null,
	profile,
	submitHandler,
	isLoadingForm,
	isSettings = false,
	setIsDirtyFormModal,
	isDirtyFormModal,
}: IOrganisationFormProps) => {
	const [organisationCheckErrors, setOrganisationCheckErrors]
		= useState<TCheckUniqOrganisations>(() => defaultValuesCheckErrors);

	const [isConfirmModal, setIsConfirmModal] = useState(false);
	const [isWarningRolesModalOpened, setIsWarningRolesModalOpened] = useState(false);
	const [isWarningUncheckTypesModalOpened, setIsWarningUncheckTypesModalOpened] = useState(false);
	const [isConfirmSaveModal, setIsConfirmSaveModal] = useState(false);
	const [isRevokedRolesModal, setIsRevokedRolesModal] = useState(false);

	const handleCloseModal = () => {
		if (isDirtyFormModal) setIsConfirmModal(true);
		else toggleOrganisationModal();
	};

	const handleCloseConfirmModal = () => setIsConfirmModal(false);

	const openConfirmSaveModal = () => setIsConfirmSaveModal(true);

	const closeConfirmSaveModal = () => setIsConfirmSaveModal(false);

	const closeRevokedRolesModal = () => setIsRevokedRolesModal(false);

	const blockTooltipText = `To ${isEditMode ? 'save' : 'add'} an organisation choose organisation type`;

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		control,
		setValue,
		watch,
		trigger,
		clearErrors,
	} = useForm<IOrganisationFormValues>({
		mode: EValidationMode.all,
		resolver: yupResolver(schema),
		defaultValues: {
			name: profile?.name || '',
			legalName: profile?.legalName || '',
			url: profile?.websiteURL || '',
			email: profile?.email || '',
			companyNumber: profile?.companyRegistrationNumber || '',
			contactNumber: profile?.phoneNumber || '',
			property: profile?.nameProperty || '',
			streetName: profile?.streetName || '',
			locality: profile?.locality || '',
			city: profile?.city || '',
			postcode: profile?.postcode || '',
			country: null,
			categoryIds: profile?.categories?.map(({ id }) => id) || [],
			avatarId: profile?.avatar?.id || null,
			organizationTelecomId: profile?.organizationTelecomId,
			timezone: null,
			roleAvailability: profile?.roleAvailability?.map(({ name }) => name) || [],
			types: profile?.types.map(({ name }) => name) || [],
		},
	});

	const roleAvailability = watch('roleAvailability');
	const types = watch('types');

	const {
		isLoading,
		countries,
		timezones,
		organisationCategories,
		organisationTypes,
		organisationRoles,
		revokedRoles,
	} = useOrganisationsFormData({
		organisationTypesForRoles: types,
		isActiveQuery: !isSettings,
		organisationId: profile?.id,
	});

	const {
		checkUniqueValuesOrganisations,
		isLoadingCheckUniqueValuesOrganisations,
	} = useMutateCheckUniqueOrganisationValues();

	const getDefaultCountry = useCallback((searchCountry: string) =>
		countries?.find((country: ICountry) => country.name === searchCountry) || null, [countries]);

	const getDefaultTimezone = useCallback((searchTimezone: string) =>
		timezones?.find((timezone: TSelectOption) => timezone?.value === searchTimezone) || null, [timezones]);

	const setRevokedRoles = () => {
		if (organisationRoles.length) {
			setValue('roleAvailability', [...roleAvailability || [], ...revokedRoles]);
		}

		closeRevokedRolesModal();
	};

	const isMNO = useMemo(() => {
		return types?.includes(EOrganizationType.networkOperator);
	}, [types]);

	useEffect(() => {
		if (types?.length === 1 && roleAvailability?.length && organisationRoles.length) {
			const roleAvailabilityByOrganisationType = getRoleAvailabilityByOrganisationType({
				roleAvailability,
				organisationRoles,
			});

			setValue('roleAvailability', roleAvailabilityByOrganisationType);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [organisationRoles, types]);

	useEffect(() => setIsDirtyFormModal(isDirty), [isDirty, setIsDirtyFormModal]);

	useEffect(() => {
		const defaultCountry = getDefaultCountry(
			(isEditMode && !!profile?.country)
				? profile?.country?.name
				: 'United Kingdom',
		);

		setValue('country', defaultCountry);
	}, [isEditMode, countries, getDefaultCountry, profile?.country, setValue]);

	useEffect(() => {
		if (!profile) return;

		const defaultTimezone = getDefaultTimezone(profile.timezone);

		setValue('timezone', defaultTimezone);
	}, [isEditMode, timezones, getDefaultTimezone, profile?.timezone, setValue, profile]);

	useEffect(() => {
		if (!isMNO && isDirty) {
			clearErrors('organizationTelecomId');
		}
	}, [clearErrors, isDirty, isMNO]);

	const submitForm = (values: IOrganisationFormValues) => {
		const submitData = {
			categoryIds: values.categoryIds,
			city: values.city || null,
			companyRegistrationNumber: values.companyNumber || null,
			countryId: values.country?.value,
			email: values.email || null,
			legalName: values.legalName,
			locality: values.locality || null,
			name: values.name,
			nameProperty: values.property || null,
			phoneNumber: values.contactNumber || null,
			postcode: values.postcode,
			streetName: values.streetName || null,
			websiteURL: values.url || null,
			avatarId: values.avatarId || null,
			roleAvailability: getRoleAvailabilityByOrganisationType({
				roleAvailability: values.roleAvailability || [],
				organisationRoles,
			}),
			timezone: values.timezone?.value,
			types: values.types,
			...isMNO
				? {
					organizationTelecomId: values?.organizationTelecomId,
				}
				: {},
		};
		if (isEditMode) {
			closeConfirmSaveModal();

			const submitCallback = () => submitHandler({
				id: profile?.id,
				values: submitData,
				closeModals,
			});

			checkUniqueValuesOrganisations({
				name: submitData.name || '',
				legalName: submitData.legalName || '',
				email: submitData.email || null,
				organizationId: profile?.id,
				successHandler: submitCallback,
				errorHandler: setOrganisationCheckErrors,
			});
		} else {
			submitHandler(submitData);
		}
	};

	const handleSaveClick = async () => {
		const isValidOrganisationTelecomId = await trigger('organizationTelecomId');

		if (!isValidOrganisationTelecomId) return;

		if (isWarningUncheckTypesModalOpened) openConfirmSaveModal();
		else await handleSubmit(submitForm)();
	};

	const isBasicInfoTabError = !!(organisationCheckErrors.nameError
		|| organisationCheckErrors.legalNameError
		|| organisationCheckErrors.emailError
		|| errors?.name
		|| errors?.legalName
		|| errors?.url
		|| errors?.email
		|| errors?.contactNumber
		|| errors?.timezone);

	const isAddressTabError = !!(errors?.property
		|| errors?.streetName
		|| errors?.locality
		|| errors?.city
		|| errors?.postcode
		|| errors?.country);

	const isOrganisationTypeTabError = !!(errors.organizationTelecomId);

	const tabs = [
		{
			title: 'Basic information',
			value: tabsNames.basicInfo,
			component: BasicInfo,
			isTabError: isBasicInfoTabError,
			props: {
				checkUniqueValues,
				organisationCheckErrors,
				setOrganisationCheckErrors,
				checkUniqueValuesOrganisations,
				register,
				errors,
				setValue,
				watch,
				trigger,
				isSettings,
				timezones,
				control,
				isBasicInfoTabError,
			},
		},
		{
			title: 'Address',
			value: tabsNames.address,
			component: Address,
			isTabError: isAddressTabError,
			props: {
				countries,
				register,
				errors,
				control,
				trigger,
				isAddressTabError,
			},
		},
		{
			title: 'Organisation type',
			value: tabsNames.organisationType,
			component: OrganisationType,
			isTabError: isOrganisationTypeTabError,
			props: {
				revokedRoles,
				setIsRevokedRolesModal,
				blockTooltipText: blockTooltipTextForPlatformOwner,
				organisationCategories,
				organisationTypes,
				register,
				errors,
				setValue,
				isMNO,
				setIsDirtyFormModal,
				trigger,
				watch,
				isWarningUncheckTypesModalOpened,
				setIsWarningUncheckTypesModalOpened,
				prevTypes: profile?.types.map(({ name }) => name),
				isOrganisationTypeTabError,
			},
		},
		{
			title: 'Role availability',
			value: tabsNames.roleAvailability,
			component: RoleAvailability,
			isTabError: false,
			props: {
				submitForm: handleSubmit(submitForm),
				setValue,
				setIsDirtyFormModal,
				trigger,
				watch,
				organisationRoles,
				isWarningRolesModalOpened,
				setIsWarningRolesModalOpened,
				prevRoleAvailability: profile?.roleAvailability?.map(({ name }) => name) || [],
				types,
			},
		},
	];

	return (
		<div className={styles.organisationForm}>
			{(isLoading || isLoadingForm || isLoadingCheckUniqueValuesOrganisations) && <SpinnerOverlay />}
			<FormWithTabs
				isEditMode={isEditMode}
				tabs={isSettings ? tabs.slice(0, -2) : tabs}
			/>
			{isEditMode && !!toggleOrganisationModal
				&& <div className={styles.buttons}>
					<Button
						onClick={handleCloseModal}
						text='Cancel'
						withoutBackground
					/>
					<Tooltip
						content={blockTooltipText}
						isDisabled={!!types?.length}
						placement='top'
					>
						<div>
							<Button
								disabled={!types?.length}
								onClick={handleSaveClick}
								text='Save'
							/>
						</div>
					</Tooltip>
				</div>
			}
			<Modal
				isOpen={isRevokedRolesModal}
				onClose={closeRevokedRolesModal}
				title='Please confirm!'
			>
				<Confirm
					isLoading={isLoading}
					onClose={closeRevokedRolesModal}
					onSubmit={setRevokedRoles}
					question={questionRevokedRoles}
				/>
			</Modal>
			<Modal
				isOpen={isConfirmModal}
				onClose={handleCloseConfirmModal}
			>
				<Confirm
					onClose={handleCloseConfirmModal}
					onSubmit={toggleOrganisationModal}
					{...modalCloseConfirm}
				/>
			</Modal>
			<Modal
				isOpen={isConfirmSaveModal && isWarningUncheckTypesModalOpened}
				onClose={closeConfirmSaveModal}
			>
				<Confirm
					onSubmit={handleSubmit(submitForm)}
					question='You are about to update the Organisation settings.'
				>
					Please confirm your action.
				</Confirm>
			</Modal>
		</div>
	);
};

export default OrganisationForm;
